import { CHARTER_EMAIL_LABELS, type CharterEmailKind } from '@sea-snag/shared';

import type { AdminCharter, CharterEmailState } from './api-client';

type FormatDate = (iso: string) => string;

const localDate: FormatDate = (iso) => new Date(iso).toLocaleDateString();

/** "20 Oct", with the year only when it is not this one. Keeps the list's email column narrow. */
export function shortDate(iso: string, now: Date = new Date()): string {
  const date = new Date(iso);
  return date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: date.getFullYear() === now.getFullYear() ? undefined : '2-digit',
  });
}

/** "follow-up", for the middle of a sentence. */
export function emailName(kind: CharterEmailKind): string {
  return CHARTER_EMAIL_LABELS[kind].toLowerCase();
}

export interface EmailCaption {
  text: string;
  tone: 'muted' | 'error';
  /** Hover text: what went wrong, and whether an earlier send got through. */
  title: string | undefined;
}

/** The line under an email's button: how its last attempt ended. */
export function emailCaption(
  state: CharterEmailState | null,
  formatDate: FormatDate = (iso) => shortDate(iso)
): EmailCaption | null {
  if (!state) return null;
  const when = formatDate(state.lastAt);

  if (state.lastStatus === 'sent') {
    return { text: `Sent ${when}`, tone: 'muted', title: undefined };
  }

  const text =
    state.lastStatus === 'opted_out'
      ? 'Unsubscribed'
      : state.lastStatus === 'check_failed'
        ? `Not sent ${when}`
        : `Failed ${when}`;
  const earlier = state.sentAt ? `Last sent ${formatDate(state.sentAt)}.` : null;
  const title =
    [state.lastError ? `${state.lastError}.` : null, earlier].filter(Boolean).join(' ') ||
    undefined;

  return { text, tone: 'error', title };
}

export interface EmailConfirmCopy {
  title: string;
  description: string;
  confirmLabel: string;
}

export function confirmEmailCopy(
  kind: CharterEmailKind,
  charter: AdminCharter,
  formatDate: FormatDate = localDate
): EmailConfirmCopy {
  const name = emailName(kind);
  const sentAt = charter.emails[kind]?.sentAt ?? null;

  const parts = [`This sends the Sea Snag ${name} to ${charter.name} at ${charter.email}.`];
  if (sentAt) {
    parts.push(`The ${name} already went out on ${formatDate(sentAt)}.`);
  }
  if (kind === 'follow_up' && !charter.emails.invite?.sentAt) {
    parts.push('They have not been sent the invite.');
  }

  return {
    title: sentAt ? `Send another ${name}?` : `Send the ${name}?`,
    description: parts.join(' '),
    confirmLabel: `Send ${name}`,
  };
}
